// @ts-nocheck
import React, {useContext} from "react";
import {ResponsiveLine} from "@nivo/line";
import ChartCard from "./ChartCard";
import StyleContext from "../../../contexts/StyleContext";
import {thresholdSweep} from "../data/fifaData";
import {FIFA_PALETTE, coinFlipMarker, getNivoTheme} from "./chartTheme";

// 4.5 Threshold sweep (line): gaps <= X are treated as a coin flip, gaps > X
// back the favorite. The curve starts at the always-bet baseline (X=0, 58.3%)
// and only declines, so no cutoff beats always backing the favorite.

// Zoomed y-range so the near-flat decline is legible.
const Y_MIN = 45;
const Y_MAX = 65;

// The always-bet baseline is the sweep's value at X = 0.
const baseline = thresholdSweep[0].winRate;

const series = [
  {
    id: "expectedWinRate",
    data: thresholdSweep.map(d => ({x: d.x, y: d.winRate}))
  }
];

const SweepTooltip = ({point}) => (
  <div className="fifa-tooltip">
    <span className="fifa-tooltip__title">Cutoff X = {point.data.x}</span>
    <span className="fifa-tooltip__row">
      <span>Expected win rate</span>
      <span>{point.data.y}%</span>
    </span>
    <span className="fifa-tooltip__caution">
      Gaps ≤ {point.data.x} treated as a coin flip.
    </span>
  </div>
);

export default function ThresholdSweep() {
  const {isDark} = useContext(StyleContext);

  const baselineMarker = {
    axis: "y",
    value: baseline,
    legend: `Always bet ${baseline}%`,
    legendPosition: "top-right",
    lineStyle: {
      stroke: FIFA_PALETTE.primary,
      strokeWidth: 1.5,
      strokeDasharray: "4 3"
    },
    textStyle: {fill: FIFA_PALETTE.primary, fontSize: 11}
  };

  const footnote = (
    <span className="fifa-legend">
      <span className="fifa-legend__item">
        <span
          aria-hidden="true"
          style={{
            display: "inline-block",
            width: 14,
            borderTop: `2px dashed ${FIFA_PALETTE.primary}`,
            marginRight: 6,
            verticalAlign: "middle"
          }}
        />
        Always-bet baseline ({baseline}%)
      </span>
      <span className="fifa-legend__item">
        <span
          aria-hidden="true"
          style={{
            display: "inline-block",
            width: 14,
            borderTop: `2px dashed ${isDark ? "#9aa4b2" : "#666"}`,
            marginRight: 6,
            verticalAlign: "middle"
          }}
        />
        Coin flip 50%
      </span>
      <span className="fifa-legend__note">
        X is the rank-gap cutoff: matches with a gap ≤ X are treated as a coin
        flip, the rest back the higher-ranked team. The curve never rises above
        58.3% (X = 0), so no cutoff beats always backing the favorite.
      </span>
    </span>
  );

  return (
    <ChartCard
      title="Threshold sweep"
      takeaway="No cutoff beats simply always backing the favorite — even close matchups carry signal."
      footnote={footnote}
    >
      <ResponsiveLine
        data={series}
        margin={{top: 20, right: 30, bottom: 60, left: 60}}
        xScale={{type: "linear", min: 0, max: 104}}
        yScale={{type: "linear", min: Y_MIN, max: Y_MAX}}
        colors={[FIFA_PALETTE.primary]}
        curve="monotoneX"
        lineWidth={2.5}
        enablePoints={false}
        enableGridX={false}
        enableArea={false}
        useMesh={true}
        enableSlices={false}
        markers={[coinFlipMarker(isDark), baselineMarker]}
        axisBottom={{
          legend: "Rank-gap cutoff X",
          legendOffset: 44,
          legendPosition: "middle",
          tickValues: [0, 20, 40, 60, 80, 100]
        }}
        axisLeft={{
          legend: "Expected win rate (%)",
          legendOffset: -46,
          legendPosition: "middle",
          tickValues: [45, 50, 55, 60, 65]
        }}
        tooltip={SweepTooltip}
        theme={getNivoTheme(isDark)}
        animate={true}
        motionConfig="gentle"
      />
    </ChartCard>
  );
}
